<template>
  <Component :is="tag"
             :name="name"
             :title="title"
             :disabled="isDisabled"
             :target="target"
             :to="to"
             :href="href"
             :type="type"
             class="UiButton"
             :class="[
               buttonSizeClass('UiButton'),
               buttonThemeClass('UiButton'),
               block ? 'UiButton--block' : '',
             ]"
  >
    <Icon v-if="prependArrow"
          name="chevron.left"
          class="UiButton__arrow"
          :class="[
            buttonSizeClass('UiButton__arrow'),
          ]"
    />
    <Icon v-if="prependIcon" :name="prependIcon" class="UiButton__icon" />
    <span v-if="$slots.default" class="UiButton__text">
      <slot />
    </span>
    <Icon v-if="appendIcon" :name="appendIcon" class="UiButton__icon" />
    <Icon v-if="appendArrow"
          name="chevron.right"
          class="UiButton__arrow"
          :class="[
            buttonSizeClass('UiButton__arrow'),
          ]"
    />
  </Component>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { RouteLocationRaw } from 'vue-router';
  import { UiSize } from '../../constants';
  import Icon from '../Icon.vue';
  import { UiButtonTag, UiButtonTarget, UiButtonTheme, UiButtonType } from './types';

  const props = withDefaults(defineProps<{
    // common props
    tag?: UiButtonTag;
    size?: UiSize.MD | UiSize.LG | UiSize.XL;
    theme?: UiButtonTheme;
    disabled?: boolean;
    prependIcon?: string;
    appendIcon?: string;
    prependArrow?: boolean;
    appendArrow?: boolean;
    name?: string;
    title?: string;
    block?: boolean;
    // component specific props
    target?: UiButtonTarget;
    to?: RouteLocationRaw;
    href?: string;
    type?: UiButtonType;
  }>(), {
    tag: UiButtonTag.BUTTON,
    size: UiSize.LG,
    theme: UiButtonTheme.LIGHT_BLUE,
    type: UiButtonType.BUTTON,
  });

  const isDisabled = computed<boolean | undefined>(() => {
    // Disable для ссылок
    if ([UiButtonTag.A, UiButtonTag.ROUTER_LINK].includes(props.tag) && !props.disabled) {
      return undefined;
    }

    return props.disabled;
  });

  const buttonSizeClass = (baseClass: string) => {
    return `${baseClass}--${props.size}`;
  };

  const buttonThemeClass = (baseClass: string) => {
    return `${baseClass}--${props.theme}`;
  };
</script>

<style scoped lang="scss">
  @import '../../assets/scss/_variables.scss';
  @import '../../assets/scss/_mixins.scss';

  .UiButton {
    @include transition($transition-function, $transition-duration, background-color, color, border-color);

    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    cursor: pointer;
    user-select: none;
    border: 1px solid transparent;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 600;
    gap: 10px;

    &[disabled] {
      background: grey;
      cursor: not-allowed;
    }

    &--block {
      display: flex;
    }

    // region SIZE
    &--md {
      padding: 6px 10px;
      line-height: 18px;
      font-size: 14px;
      height: 30px;
    }

    &--lg {
      padding: 8px 10px;
      line-height: 24px;
      font-size: 16px;
      height: 40px;
    }

    &--xl {
      padding: 12px 20px;
      line-height: 26px;
      font-size: 18px;
      height: 50px;
    }

    // endregion SIZE

    // region THEME
    &--light_blue {
      color: $color-second;
      background-color: $color-light-blue;

      &:hover {
        background-color: $color-blue-50;
      }

      &[disabled] {
        color: $color-grey-100;
        background-color: $color-grey-50;
      }
    }

    &--red {
      color: $color-white;
      background-color: $color-red-100;

      &:hover {
        background-color: $color-red-dark;
      }

      &[disabled] {
        background-color: $color-grey-100;
      }
    }

    &--navy {
      color: $color-white;
      background-color: $color-second;

      &:hover {
        background-color: $color-second-hover;
      }

      &[disabled] {
        background-color: $color-grey-100;
      }
    }

    &--white {
      color: $color-second;
      background-color: $color-white;
      border: 1px solid currentColor;

      &:hover {
        background-color: $color-light-blue;
      }

      &[disabled] {
        color: $color-grey-100;
        background-color: $color-white;
      }
    }

    &--golden {
      color: $color-second;
      background-color: $color-gold;
      border: 1px solid $color-grey-100;

      &:hover {
        color: $color-white;
        background-color: $color-second;
        border-color: $color-second;
      }

      &[disabled] {
        color: $color-white;
        background-color: $color-grey-100;
        border-color: $color-grey-100;
      }
    }

    // endregion THEME

    &__icon {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
    }

    &__arrow {
      flex-shrink: 0;
      height: 18px;

      &--md {
        width: 7px;
      }

      &--lg {
        width: 10px;
      }

      &--xl {
        width: 10px;
      }
    }

    &__text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
</style>
